import React, { useState } from 'react';
import { Card } from 'react-bootstrap';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import InsightPage, {
  StatRow, StatCard, CopyStats, useChartTheme, PHONE_CHART_HEIGHT, PHONE_CHART_MARGIN, PHONE_X_AXIS, PHONE_Y_AXIS,
} from '../../components/insights/InsightPage';
import DateWindowSelect from '../../components/insights/DateWindowSelect';
import { insightsService } from '../../services/apiService';
import { useInsightData } from '../../hooks/useInsightData';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { formatNumber } from '../../utils/format';

const SplitDays = () => {
  const isMobile = useIsMobile();
  const t = useChartTheme();
  const [days, setDays] = useState(90);
  const { data, loading, error, reload } = useInsightData(() => insightsService.splitDays(days), [days]);
  const d = data || {};
  const homeFirst = d.homeThenOffice || 0;
  const officeFirst = d.officeThenHome || 0;

  const chart = [
    { label: 'Home, then office', value: homeFirst },
    { label: 'Office, then home', value: officeFirst },
  ];

  // The lean is a finding only when both directions happen and one clearly outweighs the other
  const leanRatio = homeFirst && officeFirst ? Math.max(homeFirst, officeFirst) / Math.min(homeFirst, officeFirst) : null;
  const lean = leanRatio && leanRatio.toFixed(1) !== '1.0'
    ? (homeFirst > officeFirst
      ? `They're ${leanRatio.toFixed(1)}× more likely to start at home and finish at the office.`
      : `They're ${leanRatio.toFixed(1)}× more likely to start at the office and finish at home.`)
    : null;

  const copy = d.splitDays > 0
    ? [
      `${d.pctOfAllDays}% of hybrid days are actually "half days": people switch between home and office mid-day.`,
      lean,
      homeFirst >= officeFirst ? '☕→🏢' : '🏢→☕',
    ].filter(Boolean).join(' ')
    : '';

  return (
    <InsightPage
      title="The lunchtime commute"
      description="Days where the morning and afternoon location differ."
      controls={<DateWindowSelect value={days} onChange={setDays} />}
      loading={loading}
      error={error}
      onRetry={reload}
      ready={data != null}
      empty={!d.totalFullDays}
      emptyMessage="No home or office days in this window"
    >
      <StatRow>
        <StatCard label="Split days" value={formatNumber(d.splitDays || 0)} sub={`${d.pctOfAllDays || 0}% of all days`} variant="primary" />
        <StatCard label="Home, then office" value={formatNumber(homeFirst)} />
        <StatCard label="Office, then home" value={formatNumber(officeFirst)} />
        <StatCard label="Full days analysed" value={formatNumber(d.totalFullDays || 0)} />
      </StatRow>

      <Card className="shadow-sm">
        <Card.Header className="bg-light"><h5 className="mb-0">Direction of the mid-day switch</h5></Card.Header>
        <Card.Body>
          <ResponsiveContainer width="100%" height={isMobile ? PHONE_CHART_HEIGHT : 400}>
            <BarChart data={chart} margin={isMobile ? PHONE_CHART_MARGIN : { top: 10, right: 30, left: 10, bottom: 20 }}>
              <CartesianGrid {...t.grid} />
              <XAxis dataKey="label" {...t.xAxis} {...(isMobile ? PHONE_X_AXIS : {})} />
              <YAxis {...t.yAxis} {...(isMobile ? PHONE_Y_AXIS : {})} />
              <Tooltip {...t.tooltip} formatter={(v) => formatNumber(v)} />
              <Bar dataKey="value" name="Days" fill={t.colors.emphasis} />
            </BarChart>
          </ResponsiveContainer>
        </Card.Body>
      </Card>

      {copy && <CopyStats text={copy} />}
    </InsightPage>
  );
};

export default SplitDays;
